/**
 * Morph siluety: plynulý crossfade + volitelný SDF preload.
 */
(function (global) {
  const FILL = [34, 34, 34];
  const SDF_W = 420;
  const SDF_H = 160;
  const cache = new Map();
  let morphToken = 0;

  const DEFAULT_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  function localizeSrc(src) {
    if (!src || typeof src !== 'string') return src;
    try {
      const u = new URL(src, global.location.href);
      if (u.pathname.indexOf('/cdn/shop/') !== -1 || u.pathname.indexOf('/cdn/shopifycloud/') !== -1) {
        return global.location.origin + u.pathname + u.search;
      }
      return u.href;
    } catch {
      return src;
    }
  }

  function loadImage(src) {
    const url = localizeSrc(src);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  function samePath(a, b) {
    if (!a || !b) return false;
    const pa = localizeSrc(a);
    const pb = localizeSrc(b);
    if (pa === pb) return true;
    try {
      return new URL(pa, global.location.href).pathname === new URL(pb, global.location.href).pathname;
    } catch {
      return false;
    }
  }

  function distanceTransform(mask, w, h, seedValue) {
    const INF = 1e6;
    const d = new Float32Array(w * h);
    for (let i = 0; i < d.length; i++) d[i] = mask[i] === seedValue ? 0 : INF;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (x > 0) d[i] = Math.min(d[i], d[i - 1] + 1);
        if (y > 0) d[i] = Math.min(d[i], d[i - w] + 1);
        if (x > 0 && y > 0) d[i] = Math.min(d[i], d[i - w - 1] + 1.414);
        if (x < w - 1 && y > 0) d[i] = Math.min(d[i], d[i - w + 1] + 1.414);
      }
    }
    for (let y = h - 1; y >= 0; y--) {
      for (let x = w - 1; x >= 0; x--) {
        const i = y * w + x;
        if (x < w - 1) d[i] = Math.min(d[i], d[i + 1] + 1);
        if (y < h - 1) d[i] = Math.min(d[i], d[i + w] + 1);
        if (x < w - 1 && y < h - 1) d[i] = Math.min(d[i], d[i + w + 1] + 1.414);
        if (x > 0 && y < h - 1) d[i] = Math.min(d[i], d[i + w - 1] + 1.414);
      }
    }
    return d;
  }

  function buildSdf(img) {
    const w = SDF_W;
    const h = SDF_H;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const data = ctx.getImageData(0, 0, w, h).data;
    const mask = new Uint8Array(w * h);
    for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
      mask[i] = data[p + 3] > 40 ? 1 : 0;
    }

    const distOut = distanceTransform(mask, w, h, 1);
    const distIn = distanceTransform(mask, w, h, 0);
    const sdf = new Float32Array(w * h);
    for (let i = 0; i < sdf.length; i++) {
      sdf[i] = mask[i] ? -distIn[i] : distOut[i];
    }
    return { w, h, sdf };
  }

  async function getSdf(srcOrImg) {
    const raw = typeof srcOrImg === 'string' ? srcOrImg : srcOrImg.currentSrc || srcOrImg.src;
    if (!raw) throw new Error('missing silhouette src');
    const src = localizeSrc(raw);
    if (cache.has(src)) return cache.get(src);
    const img = await loadImage(src);
    const entry = buildSdf(img);
    cache.set(src, entry);
    return entry;
  }

  function transitionImageStyle() {
    return {
      position: 'absolute',
      inset: '0',
      zIndex: '4',
      display: 'block',
      width: '100%',
      height: 'auto',
      margin: '0',
      pointerEvents: 'none',
      willChange: 'opacity',
      transformOrigin: 'center center',
    };
  }

  /**
   * @param {HTMLElement} mediaEl
   * @param {HTMLImageElement} fromImg
   * @param {string} toSrc
   * @param {{ duration?: number, easing?: string }} [opts]
   */
  async function morphSilhouette(mediaEl, fromImg, toSrc, opts) {
    const duration = (opts && opts.duration) || 480;
    const easing = (opts && opts.easing) || DEFAULT_EASING;

    if (!mediaEl || !fromImg || !toSrc) return false;
    const fromSrc = localizeSrc(fromImg.currentSrc || fromImg.getAttribute('src') || '');
    const targetSrc = localizeSrc(toSrc);
    if (!fromSrc || !targetSrc || samePath(fromSrc, targetSrc)) return false;

    const token = ++morphToken;
    mediaEl.dataset.silMorphToken = String(token);

    fromImg.style.visibility = '';
    mediaEl.querySelectorAll('[data-sil-transition-img]').forEach((el) => el.remove());

    try {
      await loadImage(targetSrc);
    } catch {
      return false;
    }
    if (mediaEl.dataset.silMorphToken !== String(token)) return false;

    const outgoingImg = document.createElement('img');
    outgoingImg.setAttribute('data-sil-transition-img', '');
    outgoingImg.setAttribute('aria-hidden', 'true');
    outgoingImg.alt = '';
    outgoingImg.src = fromSrc;
    Object.assign(outgoingImg.style, transitionImageStyle());

    const incomingImg = document.createElement('img');
    incomingImg.setAttribute('data-sil-transition-img', '');
    incomingImg.setAttribute('aria-hidden', 'true');
    incomingImg.alt = '';
    incomingImg.src = targetSrc;
    Object.assign(incomingImg.style, transitionImageStyle(), { zIndex: '5', opacity: '0' });

    mediaEl.append(outgoingImg, incomingImg);
    fromImg.style.visibility = 'hidden';

    if (typeof outgoingImg.animate === 'function') {
      const animationOptions = { duration, easing, fill: 'forwards' };
      try {
        // staggered fade — tmavé siluety se nepřekryjou napůl (ghosting)
        await Promise.all([
          outgoingImg.animate(
            [
              { opacity: 1, offset: 0 },
              { opacity: 0, offset: 0.48 },
              { opacity: 0, offset: 1 },
            ],
            animationOptions,
          ).finished,
          incomingImg.animate(
            [
              { opacity: 0, offset: 0 },
              { opacity: 0, offset: 0.32 },
              { opacity: 1, offset: 1 },
            ],
            animationOptions,
          ).finished,
        ]);
      } catch {
        outgoingImg.remove();
        incomingImg.remove();
        if (mediaEl.dataset.silMorphToken === String(token)) fromImg.style.visibility = '';
        return false;
      }
    }

    if (mediaEl.dataset.silMorphToken !== String(token)) return false;

    fromImg.setAttribute('src', targetSrc);
    try {
      if (typeof fromImg.decode === 'function') await fromImg.decode();
    } catch {
      /* ignore */
    }
    if (mediaEl.dataset.silMorphToken !== String(token)) return false;

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (mediaEl.dataset.silMorphToken !== String(token)) return false;

    fromImg.style.visibility = '';
    outgoingImg.remove();
    incomingImg.remove();
    return true;
  }

  global.DrivetoSilhouetteMorph = {
    morphSilhouette,
    getSDF: getSdf,
    preload: getSdf,
  };
})(typeof window !== 'undefined' ? window : globalThis);
